import cx from "classnames";
import type { FC, MouseEventHandler, SVGProps } from "react";

type Size = "xsmall" | "small" | "medium" | "large";

const sizeToCSSClassesMap = {
  iconDimensions: {
    xsmall: "h-5 w-5",
    small: "h-6 w-6",
    medium: "h-6 w-6",
    large: "h-8 w-8",
  },
  padding: {
    xsmall: "p-1",
    small: "p-1.5",
    medium: "p-[10px]",
    large: "p-3",
  },
} satisfies Record<string, Record<Size, string>>;

export type IconButtonProps = {
  /** The actual svg component to render (imports from component/icon/icons) */
  "Icon": FC<SVGProps<SVGSVGElement>>;

  /** Optionally disable the button */
  "disabled"?: boolean;

  /** Callback to handle button click events */
  "onClick"?: MouseEventHandler<HTMLButtonElement>;

  /** Specify the button size */
  "size": Size;

  /** Accessible name for the icon-only button */
  "aria-label"?: string;

  "aria-busy"?: boolean;
  "aria-describedby"?: string;

  "data-testid"?: string;

  "title"?: string;
};

/**
 * Similar to `Icon`, but nested inside a button element
 */
export const IconButton = ({
  Icon,
  size,
  disabled,
  onClick,
  "aria-label": ariaLabel,
  "aria-busy": ariaBusy,
  "aria-describedby": ariaDescribedBy,
  "data-testid": dataTestId,
  title,
}: IconButtonProps) => {
  return (
    <button
      aria-busy={ariaBusy}
      aria-describedby={ariaDescribedBy}
      aria-label={ariaLabel}
      className={cx(
        "rounded-full enabled:hover:bg-border-token-outline enabled:active:bg-light-grey disabled:opacity-20",
        sizeToCSSClassesMap.padding[size],
      )}
      data-testid={dataTestId}
      disabled={disabled}
      title={title}
      type="button"
      onClick={onClick}
    >
      <Icon className={cx(sizeToCSSClassesMap.iconDimensions[size], "fill-current")} />
    </button>
  );
};
